/**
 * PipelineSettings - Stage management panel for configuring pipeline stages.
 * Supports create, edit, delete, reorder, color, probability, and won/lost flags.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Trash2, GripVertical, ChevronUp, ChevronDown,
  Save, AlertCircle, Target, Loader, Check
} from 'lucide-react';

const PRESET_COLORS = [
  '#6366f1', // indigo
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#22c55e', // green
  '#eab308', // yellow
  '#f97316', // orange
  '#ef4444', // red
  '#ec4899', // pink
  '#8b5cf6'  // violet
];

function PipelineSettings({ apiWithAccessControl, onClose, onStagesUpdated }) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(null); // stage id or 'new'
  const [hasChanges, setHasChanges] = useState(false);

  // New stage form
  const [newStage, setNewStage] = useState({
    name: '',
    color: '#6366f1',
    probability: 50,
    description: ''
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [creatingStage, setCreatingStage] = useState(false);

  // Deleting
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Auto-dismiss status
  useEffect(() => {
    if (statusMsg) {
      const timer = setTimeout(() => setStatusMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMsg]);

  // Load stages
  const loadStages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiWithAccessControl('/admin/pipeline/stages');
      const data = await res.json();
      if (data.success !== false) {
        const sorted = (data.stages || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setStages(sorted);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to load stages' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to load stages' });
      console.error('Load stages error:', err);
    } finally {
      setLoading(false);
    }
  }, [apiWithAccessControl]);

  useEffect(() => {
    loadStages();
  }, [loadStages]);

  // --- Stage Field Updates ---
  const updateStageField = (stageId, field, value) => {
    setStages(prev => prev.map(s => s.id === stageId ? { ...s, [field]: value, _dirty: true } : s));
    setHasChanges(true);
  };

  // --- Save Individual Stage ---
  const saveStage = async (stage) => {
    try {
      setSaving(true);
      const res = await apiWithAccessControl(`/admin/pipeline/stages/${stage.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: stage.name,
          color: stage.color,
          probability: Number(stage.probability) || 0,
          description: stage.description || '',
          isWon: !!stage.isWon,
          isLost: !!stage.isLost
        })
      });
      const data = await res.json();
      if (data.success !== false) {
        setStages(prev => prev.map(s => s.id === stage.id ? { ...s, _dirty: false } : s));
        setStatusMsg({ type: 'success', text: `"${stage.name}" updated` });
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to update stage' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to update stage' });
    } finally {
      setSaving(false);
    }
  };

  // --- Save All Changes ---
  const saveAllChanges = async () => {
    const dirtyStages = stages.filter(s => s._dirty);
    if (dirtyStages.length === 0) return;

    setSaving(true);
    let allSuccess = true;

    for (const stage of dirtyStages) {
      try {
        const res = await apiWithAccessControl(`/admin/pipeline/stages/${stage.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: stage.name,
            color: stage.color,
            probability: Number(stage.probability) || 0,
            description: stage.description || '',
            isWon: !!stage.isWon,
            isLost: !!stage.isLost
          })
        });
        const data = await res.json();
        if (data.success === false) {
          allSuccess = false;
        }
      } catch (err) {
        allSuccess = false;
      }
    }

    if (allSuccess) {
      setStages(prev => prev.map(s => ({ ...s, _dirty: false })));
      setHasChanges(false);
      setStatusMsg({ type: 'success', text: 'All changes saved' });
    } else {
      setStatusMsg({ type: 'error', text: 'Some changes failed to save' });
    }
    setSaving(false);
  };

  // --- Create Stage ---
  const createStage = async () => {
    if (!newStage.name.trim()) {
      setStatusMsg({ type: 'error', text: 'Stage name is required' });
      return;
    }

    try {
      setCreatingStage(true);
      const res = await apiWithAccessControl('/admin/pipeline/stages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newStage.name.trim(),
          color: newStage.color,
          probability: Number(newStage.probability) || 0,
          description: newStage.description || ''
        })
      });
      const data = await res.json();
      if (data.success !== false) {
        setStatusMsg({ type: 'success', text: `Stage "${newStage.name}" created` });
        setNewStage({ name: '', color: '#6366f1', probability: 50, description: '' });
        setShowNewForm(false);
        loadStages();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to create stage' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to create stage' });
    } finally {
      setCreatingStage(false);
    }
  };

  // --- Delete Stage ---
  const deleteStage = async (stageId) => {
    try {
      setDeletingId(stageId);
      const res = await apiWithAccessControl(`/admin/pipeline/stages/${stageId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success !== false) {
        setStages(prev => prev.filter(s => s.id !== stageId));
        setDeleteConfirmId(null);
        setStatusMsg({ type: 'success', text: 'Stage deleted' });
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Cannot delete stage (it may have opportunities)' });
        setDeleteConfirmId(null);
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to delete stage' });
      setDeleteConfirmId(null);
    } finally {
      setDeletingId(null);
    }
  };

  // --- Reorder (arrow buttons) ---
  const moveStage = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= stages.length) return;

    const newStages = [...stages];
    const [moved] = newStages.splice(index, 1);
    newStages.splice(newIndex, 0, moved);

    // Update order
    const reordered = newStages.map((s, i) => ({ ...s, order: i }));
    setStages(reordered);

    try {
      await apiWithAccessControl('/admin/pipeline/stages/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stages: reordered.map(s => ({ id: s.id, order: s.order }))
        })
      });
      setStatusMsg({ type: 'success', text: 'Stage order updated' });
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to reorder stages' });
      loadStages(); // revert
    }
  };

  // --- Toggle Won/Lost ---
  const toggleFlag = (stageId, flag) => {
    setStages(prev => prev.map(s => {
      if (s.id !== stageId) return s;
      if (flag === 'isWon') {
        return { ...s, isWon: !s.isWon, isLost: false, _dirty: true };
      } else {
        return { ...s, isLost: !s.isLost, isWon: false, _dirty: true };
      }
    }));
    setHasChanges(true);
  };

  // --- Close with save check ---
  const handleClose = () => {
    if (hasChanges) {
      onStagesUpdated && onStagesUpdated();
    } else {
      onClose();
    }
  };

  // --- Color Picker ---
  const renderColorPicker = (currentColor, onSelect, pickerId) => {
    if (colorPickerOpen !== pickerId) return null;
    return (
      <div className="mkt-pipe-color-picker" style={{ top: '100%', left: 0, marginTop: 4 }}>
        {PRESET_COLORS.map(color => (
          <div
            key={color}
            className={`mkt-pipe-color-swatch ${currentColor === color ? 'selected' : ''}`}
            style={{ backgroundColor: color }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(color);
              setColorPickerOpen(null);
            }}
          />
        ))}
      </div>
    );
  };

  // Close color picker on outside click
  useEffect(() => {
    if (colorPickerOpen === null) return;
    const handleClick = () => setColorPickerOpen(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [colorPickerOpen]);

  return (
    <div className="mkt-pipe-settings">
      {/* Header */}
      <div className="mkt-pipe-settings-header">
        <button className="mkt-pipe-btn-secondary" onClick={handleClose}>
          <ArrowLeft size={16} />
          Back
        </button>
        <h2>Pipeline Stages</h2>
        {hasChanges && (
          <button
            className="mkt-pipe-btn-primary"
            onClick={saveAllChanges}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader size={14} style={{ animation: 'mkt-pipe-spin 0.7s linear infinite' }} />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save All
              </>
            )}
          </button>
        )}
      </div>

      {/* Status */}
      {statusMsg && (
        <div className={`mkt-pipe-status-msg ${statusMsg.type}`} style={{ marginBottom: 16 }}>
          {statusMsg.type === 'success' ? <Target size={14} /> : <AlertCircle size={14} />}
          {statusMsg.text}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="mkt-pipe-loading">
          <div className="mkt-pipe-spinner" />
          <span>Loading stages...</span>
        </div>
      ) : (
        <>
          {/* Stage List */}
          <div className="mkt-pipe-settings-list">
            {stages.length === 0 && (
              <div className="mkt-pipe-empty" style={{ padding: '40px 24px' }}>
                <Target size={32} />
                <p>No stages configured</p>
                <span>Add your first pipeline stage below.</span>
              </div>
            )}
            {stages.map((stage, index) => (
              <div key={stage.id} className="mkt-pipe-settings-row">
                {/* Drag Handle */}
                <div className="drag-handle">
                  <GripVertical size={16} />
                </div>

                {/* Reorder arrows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button
                    className="mkt-pipe-btn-icon"
                    style={{ width: 22, height: 22, border: 'none', padding: 0 }}
                    onClick={() => moveStage(index, -1)}
                    disabled={index === 0}
                    title="Move up"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    className="mkt-pipe-btn-icon"
                    style={{ width: 22, height: 22, border: 'none', padding: 0 }}
                    onClick={() => moveStage(index, 1)}
                    disabled={index === stages.length - 1}
                    title="Move down"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Color */}
                <div style={{ position: 'relative' }}>
                  <div
                    className="mkt-pipe-settings-color"
                    style={{ backgroundColor: stage.color || '#94a3b8' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setColorPickerOpen(colorPickerOpen === stage.id ? null : stage.id);
                    }}
                    title="Change color"
                  />
                  {renderColorPicker(
                    stage.color,
                    (color) => updateStageField(stage.id, 'color', color),
                    stage.id
                  )}
                </div>

                {/* Name */}
                <input
                  type="text"
                  className="mkt-pipe-settings-name"
                  value={stage.name || ''}
                  onChange={(e) => updateStageField(stage.id, 'name', e.target.value)}
                  placeholder="Stage name"
                />

                {/* Probability */}
                <input
                  type="number"
                  className="mkt-pipe-settings-prob"
                  value={stage.probability ?? ''}
                  onChange={(e) => updateStageField(stage.id, 'probability', e.target.value)}
                  min="0"
                  max="100"
                  title="Probability %"
                  placeholder="%"
                />
                <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>%</span>

                {/* Won/Lost Flags */}
                <div className="mkt-pipe-settings-flags">
                  <button
                    className={`mkt-pipe-settings-flag ${stage.isWon ? 'active-won' : ''}`}
                    onClick={() => toggleFlag(stage.id, 'isWon')}
                    title="Mark as Won stage"
                  >
                    Won
                  </button>
                  <button
                    className={`mkt-pipe-settings-flag ${stage.isLost ? 'active-lost' : ''}`}
                    onClick={() => toggleFlag(stage.id, 'isLost')}
                    title="Mark as Lost stage"
                  >
                    Lost
                  </button>
                </div>

                {/* Save individual (if dirty) */}
                {stage._dirty && (
                  <button
                    className="mkt-pipe-btn-icon"
                    style={{ width: 28, height: 28, color: '#4f46e5' }}
                    onClick={() => saveStage(stage)}
                    disabled={saving}
                    title="Save changes"
                  >
                    <Check size={14} />
                  </button>
                )}

                {/* Delete */}
                {deleteConfirmId === stage.id ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="mkt-pipe-btn-danger mkt-pipe-btn-sm"
                      onClick={() => deleteStage(stage.id)}
                      disabled={deletingId === stage.id}
                      style={{ fontSize: 11, padding: '2px 8px' }}
                    >
                      {deletingId === stage.id ? '...' : 'Delete'}
                    </button>
                    <button
                      className="mkt-pipe-btn-secondary mkt-pipe-btn-sm"
                      onClick={() => setDeleteConfirmId(null)}
                      style={{ fontSize: 11, padding: '2px 8px' }}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    className="mkt-pipe-btn-icon"
                    style={{ width: 28, height: 28 }}
                    onClick={() => setDeleteConfirmId(stage.id)}
                    title="Delete stage"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add New Stage */}
          {showNewForm ? (
            <div style={{ marginTop: 16, padding: 16, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ position: 'relative' }}>
                  <div
                    className="mkt-pipe-settings-color"
                    style={{ backgroundColor: newStage.color }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setColorPickerOpen(colorPickerOpen === 'new' ? null : 'new');
                    }}
                    title="Pick color"
                  />
                  {renderColorPicker(
                    newStage.color,
                    (color) => setNewStage(prev => ({ ...prev, color })),
                    'new'
                  )}
                </div>
                <input
                  type="text"
                  className="mkt-pipe-form-input"
                  style={{ flex: 1 }}
                  placeholder="Stage name"
                  value={newStage.name}
                  onChange={(e) => setNewStage(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                <input
                  type="number"
                  className="mkt-pipe-settings-prob"
                  value={newStage.probability}
                  onChange={(e) => setNewStage(prev => ({ ...prev, probability: e.target.value }))}
                  min="0"
                  max="100"
                  placeholder="%"
                />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>%</span>
              </div>
              <input
                type="text"
                className="mkt-pipe-form-input"
                placeholder="Description (optional)"
                value={newStage.description}
                onChange={(e) => setNewStage(prev => ({ ...prev, description: e.target.value }))}
                style={{ marginBottom: 12 }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  className="mkt-pipe-btn-secondary"
                  onClick={() => {
                    setShowNewForm(false);
                    setNewStage({ name: '', color: '#6366f1', probability: 50, description: '' });
                  }}
                >
                  Cancel
                </button>
                <button
                  className="mkt-pipe-btn-primary"
                  onClick={createStage}
                  disabled={creatingStage || !newStage.name.trim()}
                >
                  {creatingStage ? (
                    <>
                      <Loader size={14} style={{ animation: 'mkt-pipe-spin 0.7s linear infinite' }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      Add Stage
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="mkt-pipe-btn-secondary"
              style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
              onClick={() => setShowNewForm(true)}
            >
              <Plus size={14} />
              Add New Stage
            </button>
          )}

          {/* Info text */}
          <div style={{ marginTop: 24, padding: 16, background: '#f8fafc', borderRadius: 8, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            <strong>Tips:</strong>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>Use the arrow buttons to reorder stages in your pipeline flow.</li>
              <li>Mark one stage as "Won" and one as "Lost" for accurate reporting.</li>
              <li>Set probability to reflect the likelihood of closing at each stage.</li>
              <li>Stages with existing opportunities cannot be deleted.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

export default PipelineSettings;
